// ============================================================================
// api_whep.ts — WHEP : lecture des paths sources SFU (MediaMTX) côté régie
//
// Mode SFU (drapeau settings.deviceTransport === 'sfu') : au lieu de négocier
// une PeerConnection P2P avec le device via la WS de signalisation, la régie
// tire le flux du device depuis son path MediaMTX src_{uuid}_{deviceId}
// (publié par le device en WHIP). Le stream obtenu est injecté dans le
// registry via le MÊME chemin que le P2P : remoteConnectionsRef +
// callbacks.injectStream('remote-{deviceId}', stream) — tout l'aval
// (vignettes, scènes, mixage) est inchangé.
//
// La WS de signalisation reste utilisée pour la présence (device_connected /
// device_disconnected) et l'intercom — seul le transport média change.
// ============================================================================

import { SignalingRefs, SignalingCallbacks } from '../../dataTypes/liveStreamManagment/types_signaling';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { cleanupDeviceBuffers, trackPeerConnection } from './api_webrtcBuffers';

// ─── Infos SFU par device (reçues dans device_connected / auth_success) ──────

export interface SfuSourceInfo {
  path:             string;
  whip_url:         string;
  whep_url:         string;
  program_whep_url: string;
}

const sfuInfoByDevice = new Map<string, SfuSourceInfo>();

/**
 * Devices pour lesquels le transport SFU s'est révélé impraticable — path
 * source annoncé mais WHEP en échec jusqu'à épuisement des tentatives.
 * Consulté par la régie avant de choisir le transport : tant que le marqueur
 * est posé, on repart en P2P plutôt que de rejouer huit tentatives vouées à
 * l'échec à chaque reconnexion.
 *
 * Volontairement NON effacé sur simple déconnexion : une reconnexion ne change
 * rien à un path cassé. Seuls une session WHEP réellement établie ou une
 * suppression définitive du device (clearDeviceSfuInfo) le lèvent.
 */
const sfuUnavailable = new Set<string>();

export const setDeviceSfuInfo = (deviceId: string, info: SfuSourceInfo): void => {
  sfuInfoByDevice.set(deviceId, info);
};

export const getDeviceSfuInfo = (deviceId: string): SfuSourceInfo | undefined =>
  sfuInfoByDevice.get(deviceId);

export const isSfuUnavailable = (deviceId: string): boolean =>
  sfuUnavailable.has(deviceId);

/**
 * Le device est-il RÉELLEMENT tiré en WHEP en ce moment ? Seule vérité sur le
 * transport d'une connexion déjà établie : le réglage `settings.deviceTransport`
 * ne s'applique qu'à la PROCHAINE connexion, et le repli P2P (bloc sfu absent ou
 * path injoignable) se décide par device sans toucher au réglage.
 *
 * Registre de module, pas un state React : l'appelant ne sera pas re-rendu par
 * son évolution. Le lire depuis un rendu déjà déclenché par un événement de
 * connexion (remoteConnections) est sûr, en dépendre pour rafraîchir ne l'est pas.
 */
export const isDeviceOnSfu = (deviceId: string): boolean =>
  whepConnections.has(deviceId);

/**
 * Oublie l'info SFU d'un device. À n'appeler que sur une suppression définitive :
 * sans ça, un device supprimé puis recréé avec le même identifiant repartirait sur
 * l'ancien bloc sfu (path MediaMTX de la session précédente).
 */
export const clearDeviceSfuInfo = (deviceId: string): void => {
  sfuInfoByDevice.delete(deviceId);
  sfuUnavailable.delete(deviceId);
};

/** Même règle path-safe que le microservice : [A-Za-z0-9-] uniquement. */
export const sanitizePathDeviceId = (deviceId: string): string =>
  deviceId.replace(/[^A-Za-z0-9-]/g, '-');

/**
 * URL WHEP d'un path source, reconstruite de zéro.
 *
 * ⚠️ NE PAS utiliser en repli quand `device_connected` n'a pas fourni de bloc
 * `sfu` — c'était l'usage d'origine, il a causé une panne en production le
 * 14/08/2026. L'absence de bloc signifie que le path MediaMTX n'existe pas :
 * l'URL reconstruite est syntaxiquement correcte mais pointe dans le vide, et
 * l'appelant croit à tort que le SFU est utilisable. Voir le garde-fou de
 * `connectToDeviceImpl` (useLiveStreamSetup.ts) : sans bloc `sfu`, on repart
 * en P2P, on ne devine pas l'URL.
 *
 * Réservé aux cas où l'on sait par ailleurs que le path existe.
 */
export const buildSourceWhepUrl = (serverUrl: string, streamPath: string, deviceId: string): string =>
  `${serverUrl}/${streamPath}/webrtc/src/${sanitizePathDeviceId(deviceId)}/whep`;

// ─── Connexions WHEP actives ─────────────────────────────────────────────────

interface WhepConnection {
  pc:       RTCPeerConnection;
  whepUrl:  string;
  retries:  number;
}

const whepConnections = new Map<string, WhepConnection>();
const whepInProgress  = new Map<string, boolean>();

const WHEP_MAX_RETRIES    = 8;
const WHEP_RETRY_BASE_MS  = 2000;

/**
 * Connecte la régie au path source d'un device en WHEP (recvonly).
 * Reproduit exactement la comptabilité du chemin P2P : remoteConnectionsRef,
 * setRemoteConnections, remoteDeviceInfoRef, injectStream.
 */
export const connectToDeviceWhep = async (
  device_id: string,
  whepUrl: string,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
): Promise<void> => {
  if (whepInProgress.get(device_id)) {
    console.log(`[WHEP] ⏭ connectToDeviceWhep ${device_id} ignoré — négociation en cours`);
    return;
  }
  whepInProgress.set(device_id, true);

  try {
    const existing = refs.remoteConnectionsRef.current.get(device_id);
    const state    = existing?.peerConnection?.connectionState;
    if (state === 'connected' || state === 'connecting') {
      console.log(`[WHEP] ⏭ connectToDeviceWhep ${device_id} ignoré (état: ${state})`);
      return;
    }
    if (existing) {
      existing.peerConnection.close();
      cleanupDeviceBuffers(device_id, refs);
    }
    // Ferme une éventuelle session WHEP précédente (sans retry)
    closeWhepConnection(device_id, callbacks, { silent: true });

    console.log(`[WHEP] ▶ Connexion WHEP → ${device_id} (${whepUrl})`);
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: `▶ WHEP → ${device_id}`, deviceId: device_id });

    const pc = new RTCPeerConnection(defaultRTCConfig());
    trackPeerConnection(pc, 'whep', device_id);
    const conn: WhepConnection = { pc, whepUrl, retries: whepConnections.get(device_id)?.retries ?? 0 };
    whepConnections.set(device_id, conn);

    pc.addTransceiver('video', { direction: 'recvonly' });
    pc.addTransceiver('audio', { direction: 'recvonly' });

    // ── Comptabilité identique au P2P ─────────────────────────────────────────
    callbacks.setRemoteConnections(prev => {
      const next = new Map(prev);
      next.set(device_id, { device_id, peerConnection: pc, stream: null });
      return next;
    });
    refs.remoteConnectionsRef.current.set(device_id, { device_id, peerConnection: pc, stream: null });

    pc.ontrack = (event) => {
      const track = event.track;
      console.log(`[WHEP] 📹 Track reçu de ${device_id}: ${track.kind} readyState=${track.readyState}`);
      callbacks.addSdpLog({ direction: 'received', type: 'event', label: `📹 Track WHEP ${track.kind} ← ${device_id}`, deviceId: device_id });

      if (track.kind === 'video') {
        let stream = event.streams[0] ?? null;
        if (!stream) {
          const receivers   = pc.getReceivers();
          const videoTrack  = receivers.find(r => r.track.kind === 'video')?.track;
          const audioTrack  = receivers.find(r => r.track.kind === 'audio')?.track;
          stream = new MediaStream([
            ...(videoTrack ? [videoTrack] : []),
            ...(audioTrack ? [audioTrack] : []),
          ]);
        }
        const updatedConn = {
          ...(refs.remoteConnectionsRef.current.get(device_id) ?? { device_id, peerConnection: pc, stream: null }),
          stream,
        };
        refs.remoteConnectionsRef.current.set(device_id, updatedConn);
        callbacks.setRemoteConnections(prev => {
          const next = new Map(prev);
          const c = next.get(device_id);
          if (c) next.set(device_id, { ...c, stream });
          return next;
        });
        if (callbacks.injectStream && stream) {
          callbacks.injectStream(`remote-${device_id}`, stream);
        }
        // L'image est là : la place n'est plus « tenue », elle est occupée.
        callbacks.clearDeviceAwaiting?.(device_id);
      }

      // Pas de lecture directe de la piste audio ici : cf. le même point dans
      // api_webrtcPeerConnection.ts. Le son du device passe uniquement par le
      // graphe Web Audio de la source `remote-<id>`.
    };

    pc.onconnectionstatechange = () => {
      const s = pc.connectionState;
      console.log(`[WHEP] 🔗 ${device_id}: ${s}`);
      const ex = refs.remoteDeviceInfoRef.current.get(device_id) || { device_id, label: device_id };
      refs.remoteDeviceInfoRef.current.set(device_id, { ...ex, connectionState: s, iceState: pc.iceConnectionState });

      if (s === 'connected') {
        const c = whepConnections.get(device_id);
        if (c && c.pc === pc) c.retries = 0;
        // Le transport SFU a fait ses preuves pour ce device : on lève un
        // éventuel marqueur d'indisponibilité laissé par une session passée.
        sfuUnavailable.delete(device_id);
        callbacks.addSdpLog({ direction: 'info', type: 'event', label: `✅ WHEP connecté — ${device_id}`, deviceId: device_id });
      }

      if (s === 'failed' || s === 'disconnected' || s === 'closed') {
        const current = refs.remoteConnectionsRef.current.get(device_id);
        if (current?.peerConnection !== pc) {
          // PC remplacée : ni sa place ni la reprise ne la concernent plus. Mais
          // une remplacée qui passe en échec doit quand même être FERMÉE —
          // l'ignorer, comme avant, la laissait ouverte (et inventoriée) pour
          // toujours, plus personne ne la tenant. `disconnected` peut encore se
          // rétablir : seul l'échec franc la condamne.
          if (s === 'failed') {
            try { pc.close(); } catch { /* déjà fermée */ }
          }
          return;
        }

        refs.remoteConnectionsRef.current.delete(device_id);
        callbacks.setRemoteConnections(prev => { const next = new Map(prev); next.delete(device_id); return next; });
        cleanupDeviceBuffers(device_id, refs);

        if (s === 'failed' || s === 'disconnected') {
          // Fermer AVANT de retenter. Sans ça, cette PeerConnection restait
          // ouverte et inventoriée pour toujours : `connectToDeviceWhep` ne ferme
          // que celle qu'il trouve dans `remoteConnectionsRef`, or on vient
          // précisément de l'en retirer trois lignes plus haut. Un appareil qui
          // se reconnecte dix fois laissait dix connexions mortes derrière lui,
          // et autant de lignes dans l'onglet Débits — toutes portant le même
          // nom, ce qui donnait l'impression que l'appareil était dupliqué.
          //
          // Sans risque : la tentative suivante crée une PC neuve de toute façon,
          // celle-ci est condamnée quoi qu'il arrive.
          try { pc.close(); } catch { /* déjà fermée */ }
          scheduleWhepRetry(device_id, whepUrl, refs, callbacks);
        }
      }
    };

    // ── Négociation WHEP (non-trickle : gathering complet puis POST) ──────────
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    await new Promise<void>(resolve => {
      if (pc.iceGatheringState === 'complete') { resolve(); return; }
      const check = () => {
        if (pc.iceGatheringState === 'complete') {
          pc.removeEventListener('icegatheringstatechange', check);
          resolve();
        }
      };
      pc.addEventListener('icegatheringstatechange', check);
      setTimeout(resolve, 5000);
    });

    const res = await fetch(whepUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/sdp' },
      body: pc.localDescription!.sdp,
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`WHEP ${res.status}${body ? ': ' + body : ''}`);
    }
    const answerSdp = await res.text();
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    console.log(`[WHEP] ✅ Answer appliquée pour ${device_id}`);
    callbacks.addSdpLog({ direction: 'received', type: 'answer', label: `Answer WHEP ← ${device_id}`, deviceId: device_id, sdp: answerSdp });

  } catch (err: any) {
    console.warn(`[WHEP] ❌ Échec connexion ${device_id}: ${err?.message ?? err}`);
    callbacks.addSdpLog({ direction: 'received', type: 'error', label: `❌ WHEP ${device_id}: ${err?.message ?? err}`, deviceId: device_id });
    // Nettoie la PC ratée puis retente — le path MediaMTX est stable côté
    // serveur, le device peut simplement ne pas encore publier (404/425).
    const c = whepConnections.get(device_id);
    if (c) { try { c.pc.close(); } catch { /* silencieux */ } }
    refs.remoteConnectionsRef.current.delete(device_id);
    callbacks.setRemoteConnections(prev => { const next = new Map(prev); next.delete(device_id); return next; });
    // La connexion s'efface — il le faut, la PC est morte et son flux avec elle : la
    // garder afficherait une dernière image figée, qui ment davantage qu'un écran noir.
    // Mais l'EMPLACEMENT, lui, est tenu le temps des retentatives.
    //
    // Sans cela, un simple 404 « le device n'a pas encore republié » — le cas le plus
    // banal d'une reprise de session — suffisait à faire sortir la caméra du deck, et
    // le remplissage automatique la relogeait ailleurs à son retour. Ce chemin-ci ne
    // passe par aucun `device_disconnected` : rien d'autre ne tenait la place ici.
    callbacks.markDeviceAwaiting?.(device_id);
    scheduleWhepRetry(device_id, whepUrl, refs, callbacks);
  } finally {
    whepInProgress.delete(device_id);
  }
};

const scheduleWhepRetry = (
  device_id: string,
  whepUrl: string,
  refs: SignalingRefs,
  callbacks: SignalingCallbacks,
): void => {
  const conn = whepConnections.get(device_id);
  const retries = (conn?.retries ?? 0) + 1;
  if (conn) conn.retries = retries;
  if (retries > WHEP_MAX_RETRIES) {
    // Abandon du SFU pour ce device. On ne s'arrête pas là : sans repli, la
    // régie n'a jamais émis d'offre P2P et le device reste bloqué « en
    // attente » sans que rien ne le signale à l'opérateur. On pose le
    // marqueur, puis on repasse par connectToDevice — le garde-fou de
    // connectToDeviceImpl le lira et routera en P2P.
    console.warn(`[WHEP] 🛑 ${device_id}: abandon après ${WHEP_MAX_RETRIES} tentatives — repli en P2P`);
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🛑 WHEP ${device_id}: abandon après ${WHEP_MAX_RETRIES} tentatives — repli en P2P`,
      deviceId: device_id,
    });
    sfuUnavailable.add(device_id);
    whepConnections.delete(device_id);
    callbacks.connectToDevice(device_id).catch(err =>
      console.warn(`[WHEP] repli P2P impossible pour ${device_id}: ${err?.message ?? err}`)
    );
    return;
  }
  const delay = Math.min(WHEP_RETRY_BASE_MS * retries, 15000);
  console.log(`[WHEP] 🔄 ${device_id}: nouvelle tentative dans ${delay}ms (${retries}/${WHEP_MAX_RETRIES})`);
  setTimeout(() => {
    // Ne pas retenter si le device a été déconnecté entre-temps (session fermée)
    if (!whepConnections.has(device_id)) return;
    connectToDeviceWhep(device_id, whepUrl, refs, callbacks);
  }, delay);
};

/**
 * Ferme la session WHEP d'un device (DELETE sur l'endpoint, comme le WHIP)
 * et stoppe les retries. À appeler sur device_disconnected et au démontage.
 */
export const closeWhepConnection = (
  device_id: string,
  callbacks?: Pick<SignalingCallbacks, 'addSdpLog'>,
  opts?: { silent?: boolean },
): void => {
  const conn = whepConnections.get(device_id);
  if (!conn) return;
  whepConnections.delete(device_id);
  try { conn.pc.close(); } catch { /* silencieux */ }
  try { fetch(conn.whepUrl, { method: 'DELETE' }); } catch { /* silencieux */ }
  if (!opts?.silent) {
    console.log(`[WHEP] 🔌 Session WHEP fermée pour ${device_id}`);
    callbacks?.addSdpLog?.({ direction: 'info', type: 'event', label: `🔌 WHEP fermé — ${device_id}`, deviceId: device_id });
  }
};

export const closeAllWhepConnections = (): void => {
  Array.from(whepConnections.keys()).forEach(id => closeWhepConnection(id, undefined, { silent: true }));
};
